import { useState, useEffect } from "react";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import {
  Loader2,
  Package,
  FileText,
  Clock,
  CheckCircle,
  Eye
} from "lucide-react";
import { Button } from "@/components/ui/button";

export default function OrdersManagement() {
  const { token } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await fetch(`${API}/orders/requests`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setRequests(data.requests || []);
        }
      } catch (error) {
        console.error("Failed to fetch orders:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [token]);

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-amber-100 text-amber-700",
      in_progress: "bg-blue-100 text-blue-700",
      completed: "bg-emerald-100 text-emerald-700",
      cancelled: "bg-red-100 text-red-700"
    };
    return styles[status] || "bg-slate-100 text-slate-600";
  };

  const getTypeBadge = (type) => {
    const styles = {
      maintenance: "bg-purple-100 text-purple-700",
      supply: "bg-blue-100 text-blue-700",
      emergency: "bg-red-100 text-red-700",
      installation: "bg-emerald-100 text-emerald-700"
    };
    return styles[type] || "bg-slate-100 text-slate-600";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Order Requests</h2>
          <p className="text-slate-500">View and manage incoming orders</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-4 gap-4">
        <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-700">{requests.filter(r => r.status === 'pending').length}</p>
              <p className="text-sm text-amber-600">Pending</p>
            </div>
          </div>
        </div>
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Package className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-700">{requests.filter(r => r.status === 'in_progress').length}</p>
              <p className="text-sm text-blue-600">In Progress</p>
            </div>
          </div>
        </div>
        <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-700">{requests.filter(r => r.status === 'completed').length}</p>
              <p className="text-sm text-emerald-600">Completed</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-50 border border-slate-100 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-700">{requests.length}</p>
              <p className="text-sm text-slate-600">Total Orders</p>
            </div>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">ID</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Facility</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Contact</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {requests.map((req) => (
              <tr key={req.id} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-mono text-sm text-slate-900">#{req.id?.slice(0, 8)}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeBadge(req.type)}`}>
                    {req.type?.toUpperCase() || "ORDER"}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-slate-600">{req.hospital_name || req.facility || "—"}</td>
                <td className="px-4 py-3">
                  <div>
                    <p className="text-sm text-slate-900">{req.contact_name || "—"}</p>
                    <p className="text-xs text-slate-500">{req.contact_email || ""}</p>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(req.status)}`}>
                    {req.status?.toUpperCase() || "PENDING"}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-slate-500">
                  {req.created_at ? new Date(req.created_at).toLocaleDateString() : "—"}
                </td>
                <td className="px-4 py-3">
                  <Button size="sm" variant="ghost">
                    <Eye className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {requests.length === 0 && (
          <div className="py-12 text-center text-slate-500">
            <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>No order requests yet</p>
          </div>
        )}
      </div>
    </div>
  );
}
